import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import i18n from "i18next";
import slugify from "slugify";
import { LANG_ARR } from "../../lang";
import { ROLES } from "../../common/roles";
import { DOCTOR_ROLES, QUESTION_TYPE, RESOLUTION_NAME } from "./constants";

dayjs.extend(localizedFormat);

export function shadeColor(color, percent) {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = Math.floor((R * (100 + percent)) / 100);
  G = Math.floor((G * (100 + percent)) / 100);
  B = Math.floor((B * (100 + percent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR =
    R.toString(16).length === 1 ? "0" + R.toString(16) : R.toString(16);
  const GG =
    G.toString(16).length === 1 ? "0" + G.toString(16) : G.toString(16);
  const BB =
    B.toString(16).length === 1 ? "0" + B.toString(16) : B.toString(16);

  return "#" + RR + GG + BB;
}

/**
 * TODO: this is currently an O(N**2) function, don't use with peer lists, it's currently
 * being used to find intersection between list of role names where the complexity shouldn't matter much.
 */
export const arrayIntersection = (a, b) => {
  if (a === undefined || b === undefined) {
    return [];
  }
  // ensure "a" is the bigger array
  if (b.length > a.length) {
    let t = b;
    b = a;
    a = t;
  }
  return a.filter(function (e) {
    return b.indexOf(e) > -1;
  });
};

export const getMetadata = metadataString => {
  try {
    return metadataString === "" ? {} : JSON.parse(metadataString);
  } catch (error) {
    return {};
  }
};

export const metadataProps = function (peer) {
  return {
    isHandRaised: getMetadata(peer.metadata)?.isHandRaised,
  };
};

export const isScreenshareSupported = () => {
  return typeof navigator.mediaDevices.getDisplayMedia !== "undefined";
};

export const getRoutePrefix = () => {
  return window.location.pathname.startsWith("/streaming") ? "/streaming" : "";
};

export const isStreamingKit = () => {
  return window.location.pathname.startsWith("/streaming");
};

export const isInternalRole = role => role && role.startsWith("__internal");

export const metadataPayloadParser = payload => {
  try {
    const data = window.atob(payload);
    const parsedData = JSON.parse(data);
    return parsedData;
  } catch (e) {
    return { payload };
  }
};

export const isValidURL = url => {
  const urlPattern = new RegExp(/^(ftp|http|https):\/\/[^ "]+$/);
  return !!urlPattern.test(url);
};

const compareArrays = (a, b) => {
  if (a.length !== b.length) return false;
  else {
    // Comparing each element of your array
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }
};

export const checkCorrectAnswer = (answer, localPeerResponse, type) => {
  if (type === QUESTION_TYPE.SINGLE_CHOICE) {
    return answer?.option === localPeerResponse?.option;
  } else if (type === QUESTION_TYPE.MULTIPLE_CHOICE) {
    return (
      answer?.options &&
      localPeerResponse?.options &&
      compareArrays(answer?.options, localPeerResponse?.options)
    );
  }
};

export const isValidTextInput = (text, minLength = 2, maxLength = 100) => {
  return text && text.length >= minLength && text.length <= maxLength;
};

export const convertSlug = (text, replacement = "-") => {
  if (!text) return;
  return slugify(text, {
    replacement, // replace spaces with replacement character, defaults to `-`
    remove: /[*+~.()'"!:@]/g, // remove characters that match regex, defaults to `undefined`
    lower: true, // convert to lower case, defaults to `false`
    strict: true, // strip special characters except replacement, defaults to `false`
    locale: "vi", // language code of the locale to use
  });
};

export const convertRolesName = roleName => {
  if (!roleName || !roleName.length) {
    return "";
  }
  const role = convertSlug(roleName, "-");
  return role;
};

export const getImageURL = path => {
  return process.env.REACT_APP_BASE_IMAGE_URL + "/storage/" + path;
};

export const isJSON = data => {
  try {
    JSON.parse(data);
  } catch (error) {
    return false;
  }
  return true;
};

export const convertResolutionName = (resolutionName, t) => {
  if (!resolutionName || !resolutionName.length) {
    return "";
  }
  const name = RESOLUTION_NAME[resolutionName.toUpperCase()];
  if (t instanceof Function) return t(name);
  return i18n.t(name);
};

export const getLanguage = (langCode, languages) => {
  if (!langCode) return;
  const languagesRaw = languages || LANG_ARR;
  const lang = languagesRaw?.find(item => item.code === langCode);
  return lang;
};

export const getRoleGS = role => {
  return ROLES[role?.replaceAll("-", "_")?.toUpperCase()];
};

export const getDoctorRole = role => {
  switch (role) {
    case DOCTOR_ROLES.PRESIDE:
      return "lbl_role_preside";
    case DOCTOR_ROLES.MEMBER:
      return "lbl_role_member";
    case DOCTOR_ROLES.EXPERT:
      return "lbl_role_expert";
    case DOCTOR_ROLES.SECRETARY:
      return "lbl_role_secretary";
    default:
      return "";
  }
};

export const getHMDMYVN = (date, langCode) => {
  const lang = langCode || window.localStorage.getItem("lang");
  const string = dayjs(date).locale(lang);
  if (lang === "vi") {
    const dt = dayjs(date);
    // let hm = dt.clone().format('HH:mm')
    const d = dt.clone().format("DD");
    const m = dt.clone().format("MM");
    const y = dt.clone().format("YYYY");
    return `Ngày ${d} tháng ${m} năm ${y}`;
  } else {
    return string.format("LLL");
  }
};

export const getDateTime = (date, langCode) => {
  const lang = langCode || window.localStorage.getItem("lang");
  const string = dayjs(date).locale(lang);
  if (lang === "vi") {
    const dt = dayjs(date);
    const hm = dt.clone().format("HH:mm");
    const d = dt.clone().format("DD");
    const m = dt.clone().format("MM");
    const y = dt.clone().format("YYYY");
    return `${hm}, ngày ${d} tháng ${m} năm ${y}`;
  } else {
    return string.format("LLL");
  }
};

export const ENVELOPE_DOCUMENT_TYPE = {
  EXAMINATION: 1, // PHIẾU KHÁM
  PRESCRIPTION: 2, // ĐƠN THUỐC
  INDICATION_RESULT: 3, // CHỈ ĐỊNH
  SURGERY_FORM: 4, // PHIẾU PHẪU THUẬT THỦ THUẬT
  TREATMENT_FORM: 5, // Phiếu điều trị
  CARE_FORM: 6, // Phiếu chăm sóc
  NUTRITIONAL_FORM: 7, // Phiếu đánh giá dinh dưỡng
  CONFIRM_TREATMENT: 8, // Phiếu xác nhận điều trị
  ADMINISTRATIVE_FORM: 9, // Biểu mẫu hành chính
  SUMMARY_TREATMENT_FORM: 10, // Phiếu sơ kết điều trị
  MDT_FORM: 11, // Biên bản hội chẩn
  PRE_OPERATIVE_ANESTHESIA_FORM: 12, // Phiếu gây mê trước mổ
  OPERATION_FORM: 13, // Tóm tắt thông qua mổ
};

export const ENVELOPE_TYPE = {
  SELF_SIGN: 1,
  SEND_SIGN: 2,
};
